/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: menu.ts
 🗓️ Created: 2025-01-05
 🧩 Description:
    Menu store for managing navigation state and menu filtering.
    Uses hardcoded menu definition (menuDefinition.ts) instead of
    database queries for instant rendering.

 🔧 Notes:
    - Menu structure is defined statically in menuDefinition.ts
    - Filtering by tenant (mainTenantOnly) happens at initialization
    - Permission filtering uses slug-based checks from auth store
    - No API calls needed for menu rendering

 🔁 Last Updated: 2026-02-15 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { MenuItemDef, buildFilteredMenu, findAncestorChain, MENU_DEFINITION } from '../config/menuDefinition';

export interface MenuItem {
  id: number;
  uuid: string;
  moduleId?: string | null;
  title: string;
  slug?: string;
  description?: string;
  icon?: string;
  url?: string;
  level: number;
  parentId?: number | null;
  order: number;
  isActive: boolean;
  hasChildren?: boolean;
  children?: MenuItem[];
  isChildrenLoaded?: boolean;
  external?: boolean;
}

interface MenuState {
  menuItems: MenuItem[];
  level1Items: MenuItem[];
  expandedItems: Set<string>;  // Now uses slug strings instead of numeric IDs
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;
}

interface MenuActions {
  initializeMenu: (tenantSlug: string) => void;
  toggleExpanded: (itemSlug: string, closeOthersAtSameLevel?: boolean) => void;
  expandForPath: (path: string) => void;
  clearError: () => void;
  reset: () => void;
  getFinalItems: () => MenuItem[];
  // Legacy compatibility
  loadMenuItems: () => Promise<void>;
  loadLevel1: () => Promise<void>;
  loadChildren: (parentId: number) => Promise<void>;
  loadMenuForPath: (path: string) => Promise<void>;
}

type MenuStore = MenuState & MenuActions;

// Auto-increment counter for generating stable numeric IDs from slugs
let idCounter = 1;
const slugToIdMap = new Map<string, number>();

function getStableId(slug: string): number {
  if (!slugToIdMap.has(slug)) {
    slugToIdMap.set(slug, idCounter++);
  }
  return slugToIdMap.get(slug)!;
}

/**
 * Convert MenuItemDef (hardcoded) to MenuItem (store format)
 * for backward compatibility with Sidebar.tsx rendering
 */
function defToMenuItem(def: MenuItemDef, parentSlug?: string): MenuItem {
  const id = getStableId(def.slug);
  const parentId = parentSlug ? getStableId(parentSlug) : null;
  const children = def.children?.map(child => defToMenuItem(child, def.slug)) || [];

  return {
    id,
    uuid: def.slug,
    moduleId: def.slug,
    title: def.slug, // Will be translated by Sidebar via i18n
    slug: def.slug,
    description: def.slug,
    icon: def.icon,
    url: def.url,
    level: def.level,
    parentId,
    order: 0,
    isActive: true,
    hasChildren: children.length > 0,
    children,
    isChildrenLoaded: true, // Always loaded since hardcoded
    external: def.external,
  };
}

import { useAuthStore } from './auth';

export const useMenuStore = create<MenuStore>()(
  persist(
    (set, get) => ({
      // State
      menuItems: [],
      level1Items: [],
      expandedItems: new Set<string>(),
      isLoading: false,
      error: null,
      isInitialized: false,

      /**
       * Initialize menu from hardcoded definition.
       * Filters by tenant visibility (no API calls needed).
       */
      initializeMenu: (tenantSlug: string) => {
        const isMainTenant = tenantSlug === 'main';
        const user = useAuthStore.getState().user as any;

        // Build permission map for non-primary admins
        let permissionSlugs: Map<string, string> | undefined = undefined;
        if (user && !user.isPrimaryAdmin) {
          permissionSlugs = new Map<string, string>();
          const levelHierarchy: Record<string, number> = { 'NONE': 0, 'READ': 1, 'WRITE': 2, 'FULL': 3 };

          user.roles?.forEach((role: any) => {
            role.permissions?.forEach((perm: any) => {
               // Support both moduleSlug and legacy moduleId
              const slug = perm.moduleSlug || perm.moduleName || String(perm.moduleId || '');
              if (slug) {
                const currentLevel = permissionSlugs!.get(slug) || 'NONE';
                const newLevel = perm.level || 'NONE';
                // Only override if the new level is higher in the hierarchy
                if (levelHierarchy[newLevel] > levelHierarchy[currentLevel] || !permissionSlugs!.has(slug)) {
                  permissionSlugs!.set(slug, newLevel);
                }
              }
            });
          });
        }

        // Build filtered menu tree
        const filteredDefs = buildFilteredMenu(isMainTenant, permissionSlugs, user?.availableApps);

        // Convert to MenuItem format for backward compatibility
        const menuItems = filteredDefs.map(def => defToMenuItem(def));
        const level1Items = menuItems.filter(item => item.level === 1);

        // Preserve existing expanded items
        const currentExpanded = get().expandedItems;

        set({
          menuItems,
          level1Items,
          expandedItems: currentExpanded,
          isLoading: false,
          isInitialized: true,
          error: null,
        });
      },

      /**
       * Toggle expanded state for a menu item (by slug).
       */
      toggleExpanded: (itemSlug: string, closeOthersAtSameLevel: boolean = false) => {
        const state = get();
        const newExpanded = new Set(state.expandedItems);

        if (newExpanded.has(itemSlug)) {
          newExpanded.delete(itemSlug);
        } else {
          // Find the item to determine its level
          const findItem = (items: MenuItem[]): MenuItem | null => {
            for (const item of items) {
              if (item.slug === itemSlug) return item;
              if (item.children) {
                const found = findItem(item.children);
                if (found) return found;
              }
            }
            return null;
          };

          const item = findItem(state.menuItems);

          if (closeOthersAtSameLevel && item) {
            if (item.level === 1) {
              // Close all other level 1 items
              state.menuItems.forEach(menuItem => {
                if (menuItem.level === 1 && menuItem.slug !== itemSlug) {
                  newExpanded.delete(menuItem.slug!);
                }
              });
            } else {
              // Close siblings (same parent, same level)
              const findSiblings = (items: MenuItem[]): void => {
                for (const menuItem of items) {
                  if (menuItem.children) {
                    menuItem.children.forEach(child => {
                      if (child.parentId === item.parentId && child.slug !== itemSlug && child.level === item.level) {
                        newExpanded.delete(child.slug!);
                      }
                    });
                    findSiblings(menuItem.children);
                  }
                }
              };
              findSiblings(state.menuItems);
            }
          }

          newExpanded.add(itemSlug);
        }

        set({ expandedItems: newExpanded });
      },

      /**
       * Expand menu items based on current URL path.
       * Uses the hardcoded definition to find ancestors instantly.
       */
      expandForPath: (path: string) => {
        const state = get();
        const chain = findAncestorChain(path);

        if (chain.length > 0) {
          const newExpanded = new Set(state.expandedItems);

          // Add all ancestors to expanded set
          chain.forEach(item => {
            if (item.level === 1 || item.level === 2) {
              newExpanded.add(item.slug);
            }
          });

          set({ expandedItems: newExpanded });
        }
      },

      clearError: () => {
        set({ error: null });
      },

      reset: () => {
        set({
          menuItems: [],
          level1Items: [],
          expandedItems: new Set<string>(),
          isLoading: false,
          error: null,
          isInitialized: false,
        });
      },

      /**
       * Get all final items (level 3) from the menu tree.
       */
      getFinalItems: () => {
        const state = get();
        const finalItems: MenuItem[] = [];

        const collect = (items: MenuItem[]) => {
          for (const item of items) {
            if (item.level === 3) finalItems.push(item);
            if (item.children) collect(item.children);
          }
        };

        collect(state.menuItems);
        return finalItems;
      },

      // ─── Legacy Compatibility (no-ops, kept for consumers that reference these) ───

      loadMenuItems: async () => {
        const state = get();
        if (!state.isInitialized) {
          // Auto-initialize with 'main' tenant as fallback
          state.initializeMenu('main');
        }
      },

      loadLevel1: async () => {
        const state = get();
        if (!state.isInitialized) {
          state.initializeMenu('main');
        }
      },

      loadChildren: async (_parentId: number) => {
        // No-op: all children are already loaded in hardcoded menu
      },

      loadMenuForPath: async (path: string) => {
        const state = get();
        if (!state.isInitialized) {
          state.initializeMenu('main');
        }
        state.expandForPath(path);
      },
    }),
    {
      name: 'menu-storage',
      partialize: (state) => ({
        expandedItems: Array.from(state.expandedItems), // Persist as array of slugs
      }),
      onRehydrateStorage: () => (state) => {
        // Convert from array to Set when restoring
        if (state && Array.isArray(state.expandedItems)) {
          state.expandedItems = new Set(state.expandedItems as unknown as string[]);
        }
      }
    }
  )
);
